import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, Tag, Users, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const links = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testId: "nav-overview" },
  { to: "/admin/products", label: "Products", icon: Package, testId: "nav-products" },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart, testId: "nav-orders" },
  { to: "/admin/coupons", label: "Coupons", icon: Tag, testId: "nav-coupons" },
  { to: "/admin/customers", label: "Customers", icon: Users, testId: "nav-customers" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/admin-login", { replace: true });
  };

  return (
    <div className="theme-admin min-h-screen bg-[color:var(--admin-bg)] text-[color:var(--admin-ink)] flex"
         data-testid="admin-layout">
      <aside className="hidden md:flex md:w-64 flex-col border-r border-white/5 bg-slate-900/40 backdrop-blur">
        <div className="p-6 border-b border-white/5">
          <div className="font-serif-display text-2xl">ATSHREE</div>
          <div className="overline text-amber-400">Admin Console</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {links.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-amber-500/15 text-amber-300 border border-amber-500/20"
                    : "text-slate-300 hover:bg-white/5"
                }`
              }
            >
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5 space-y-2">
          <a href="/" target="_blank" rel="noreferrer"
             className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-200"
             data-testid="admin-view-store">
            <ExternalLink className="h-3.5 w-3.5" /> View storefront
          </a>
          <div className="text-xs text-slate-500 font-mono-accent break-all">{user?.email}</div>
          <Button variant="ghost" onClick={handleLogout}
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/5"
            data-testid="admin-logout">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col">
        <header className="md:hidden flex items-center justify-between p-4 border-b border-white/5">
          <div className="font-serif-display text-lg">ATSHREE Admin</div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-mobile"
            className="text-slate-300">Sign out</Button>
        </header>

        <div className="md:hidden flex overflow-x-auto gap-1 p-2 border-b border-white/5">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} end={l.end}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded text-xs whitespace-nowrap ${
                  isActive ? "bg-amber-500/15 text-amber-300" : "text-slate-400"
                }`
              }
            >{l.label}</NavLink>
          ))}
        </div>

        <div className="flex-1 p-4 sm:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
